import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mic, Square, Trash2, Upload, Send, Scale, FileText, Camera } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { useTranslation } from 'react-i18next';

const SendFeedbackPage = () => {
  const { reportId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(true);
  const [text, setText] = useState('');
  const [audio, setAudio] = useState(null);
  const [recording, setRecording] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const audioInputRef = useRef(null);

  useEffect(() => {
    apiFetch(`/api/reports/${reportId}`)
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        setReport(data);
        if (data?.feedback?.text) setText(data.feedback.text);
        if (data?.feedback?.audio) setAudio(data.feedback.audio);
      })
      .catch(() => {})
      .finally(() => setLoadingReport(false));
  }, [reportId]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream);
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        reader.onload = (ev) => setAudio(ev.target.result);
        reader.readAsDataURL(blob);
        stream.getTracks().forEach((tr) => tr.stop());
      };
      mr.start();
      mediaRecorderRef.current = mr;
      setRecording(true);
    } catch {
      setError(t('reports.mic_error'));
    }
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setRecording(false);
  };

  const handleAudioFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setAudio(ev.target.result);
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSubmit = async () => {
    setError('');
    if (!text.trim() && !audio) { setError(t('reports.error_feedback_empty')); return; }
    setSending(true);
    try {
      const res = await apiFetch(`/api/reports/${reportId}/feedback`, {
        method: 'POST',
        body: JSON.stringify({ text: text.trim() || null, audio }),
      });
      if (res.ok) {
        navigate(`/reports/${reportId}`, { replace: true });
      } else {
        const d = await res.json();
        setError(d.detail || t('common.error_connection'));
      }
    } catch {
      setError(t('common.error_connection'));
    } finally {
      setSending(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  if (loadingReport) return <div className="loading"><div className="loading-spinner" /></div>;

  const isUpdate = !!report?.feedback;

  return (
    <div className="page-container animate-fade-in">
      {/* Header */}
      <div className="header" style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            className="btn btn-icon btn-secondary"
            onClick={() => navigate(-1)}
            style={{ flexShrink: 0 }}
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <p className="text-muted" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              {report?.athlete_name || t('reports.inbox_title')}
            </p>
            <h1 className="header-title">
              {isUpdate ? t('reports.update_feedback_btn') : t('reports.feedback_title')}
            </h1>
          </div>
        </div>
        <img src="/dragon-logo.png" alt="DragonFit" className="header-logo" />
      </div>

      {/* Resumen del reporte del atleta */}
      {report && (
        <div className="card mb-4" style={{ background: 'rgba(var(--primary-rgb),0.05)', border: '1px solid rgba(var(--primary-rgb),0.2)' }}>
          <p style={{ fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--primary)', marginBottom: '10px' }}>
            {t('reports.detail_title')} · {formatDate(report.created_at)}
          </p>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', marginBottom: report.notes ? '12px' : 0 }}>
            {report.weight != null && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'var(--foreground)', background: 'var(--secondary)', padding: '4px 10px', borderRadius: '20px' }}>
                <Scale size={12} style={{ color: 'var(--primary)' }} />
                {report.weight} kg
              </span>
            )}
            {report.photos?.length > 0 && (
              <span style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '13px', color: 'var(--foreground)', background: 'var(--secondary)', padding: '4px 10px', borderRadius: '20px' }}>
                <Camera size={12} style={{ color: 'var(--primary)' }} />
                {report.photos.length} {t('reports.photos_label').toLowerCase()}
              </span>
            )}
          </div>

          {report.notes && (
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(var(--primary-rgb),0.15)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <FileText size={12} style={{ color: 'var(--muted-foreground)' }} />
                <span style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted-foreground)' }}>
                  {t('reports.notes_label')}
                </span>
              </div>
              <p style={{
                fontSize: '13px', color: 'var(--foreground)', lineHeight: '1.6',
                overflow: 'hidden', display: '-webkit-box',
                WebkitLineClamp: 4, WebkitBoxOrient: 'vertical',
              }}>
                {report.notes}
              </p>
            </div>
          )}

          {report.audio && (
            <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid rgba(var(--primary-rgb),0.15)' }}>
              <p style={{ fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--muted-foreground)', marginBottom: '6px' }}>
                {t('reports.audio_label')}
              </p>
              <audio src={report.audio} controls style={{ width: '100%', height: '36px' }} />
            </div>
          )}
        </div>
      )}

      {/* Texto del feedback */}
      <div className="form-group">
        <label className="form-label">{t('reports.feedback_text_label')}</label>
        <textarea
          className="input"
          rows={7}
          placeholder={t('reports.feedback_text_placeholder')}
          value={text}
          onChange={(e) => setText(e.target.value)}
          style={{ resize: 'vertical', lineHeight: '1.7' }}
          autoFocus
        />
      </div>

      {/* Audio del feedback */}
      <div className="form-group">
        <label className="form-label">
          {t('reports.audio_label')}
          <span style={{ color: 'var(--muted-foreground)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
            {' '}{t('common.optional')}
          </span>
        </label>

        {audio ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <audio src={audio} controls style={{ flex: 1, height: '40px' }} />
            <button className="btn btn-sm btn-secondary" onClick={() => setAudio(null)}>
              <Trash2 size={14} />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className={`btn btn-sm ${recording ? 'btn-danger' : 'btn-secondary'}`}
              onClick={recording ? stopRecording : startRecording}
              style={{ flex: 1 }}
            >
              {recording
                ? <><Square size={14} /> {t('reports.stop_rec')}</>
                : <><Mic size={14} /> {t('reports.start_rec')}</>
              }
            </button>
            <button
              className="btn btn-sm btn-secondary"
              onClick={() => audioInputRef.current?.click()}
              style={{ flex: 1 }}
            >
              <Upload size={14} /> {t('reports.upload_audio')}
            </button>
            <input
              ref={audioInputRef}
              type="file"
              accept="audio/*"
              style={{ display: 'none' }}
              onChange={handleAudioFile}
            />
          </div>
        )}

        {recording && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '10px' }}>
            <span style={{
              width: '8px', height: '8px', borderRadius: '50%',
              background: '#ef4444', display: 'inline-block',
              animation: 'pulse 1s infinite',
            }} />
            <span style={{ fontSize: '13px', color: '#ef4444' }}>{t('reports.recording')}</span>
          </div>
        )}
      </div>

      {error && (
        <p style={{ color: '#ef4444', fontSize: '14px', marginBottom: '16px', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: '8px', border: '1px solid rgba(239,68,68,0.3)' }}>
          {error}
        </p>
      )}

      {/* Botones */}
      <div style={{ display: 'flex', gap: '10px', paddingBottom: '24px' }}>
        <button className="btn btn-secondary" style={{ flex: 1 }} onClick={() => navigate(-1)}>
          {t('common.cancel')}
        </button>
        <button
          className="btn btn-primary"
          style={{ flex: 2 }}
          onClick={handleSubmit}
          disabled={sending}
        >
          {sending
            ? t('reports.sending')
            : <><Send size={16} /> {isUpdate ? t('reports.update_feedback_btn') : t('reports.send_feedback_btn')}</>
          }
        </button>
      </div>
    </div>
  );
};

export default SendFeedbackPage;
